import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ReuseableTable from '../table/ReuseableTable';
import useGetGiftCards from '../../hooks/giftCards/useGetGiftCards';
import useGetGiftCardsOverview from '../../hooks/giftCards/useGetGiftCardsOverview';
import { Row } from '../../interfaces';
import { getFullName } from '../../helpers/global';
import { formatDollarAmount } from '../../utils/global/helper';
import TableDropdownMenu from '../global/TableDropdownMenu';
import useInfiniteQueryPage from '../../hooks/global/useInfiniteQueryPage';
import useSort from '../../hooks/global/useSort';
import { SORTABLE_KEYS } from '../../constants/gift-cards';
import useGiftCardActions from '../../hooks/giftCards/useGiftCardActions';
import GiftCardDetailsModal from './GiftCardDetailsModal';

interface Props {
  setIsSettingsOpen: (isOpen: boolean) => void;
}

function GiftCardsTable({ setIsSettingsOpen }: Props) {
  const { data: overview } = useGetGiftCardsOverview();
  const sort = useSort(SORTABLE_KEYS);
  const {
    giftCards,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetching,
    totalCount,
  } = useGetGiftCards({
    enabled: !!overview?.hasGiftCards,
    sort,
  });

  const {
    page,
    onPageChange,
    rowsPerPage,
    displayedItems,
    shouldShowPagination,
  } = useInfiniteQueryPage({
    data: giftCards,
    hasNextPage,
    fetchNextPage,
    isFetching,
  });

  const [detailsGiftCardId, setDetailsGiftCardId] = useState<string | null>(
    null,
  );

  const { getOptions } = useGiftCardActions();

  const rows = useMemo<Row[]>(
    () =>
      displayedItems.map(giftCard => ({
        rowId: giftCard.id,
        cells: [
          (giftCard.customer?.user && getFullName(giftCard.customer.user)) ||
            '-',
          'Digital',
          giftCard.purchasedAt
            ? format(new Date(giftCard.purchasedAt), 'MM/dd/yyyy')
            : 'N/A',
          giftCard.lastUsedAt
            ? format(new Date(giftCard.lastUsedAt), 'MM/dd/yyyy')
            : 'N/A',
          formatDollarAmount(giftCard.currentBalance / 100, true),
          giftCard.expiresAt
            ? format(new Date(giftCard.expiresAt), 'MM/dd/yyyy')
            : 'N/A',
          <TableDropdownMenu
            options={[
              {
                id: 'view',
                label: 'View code',
                action: () => setDetailsGiftCardId(giftCard.id),
              },
              ...getOptions(giftCard),
            ]}
          />,
        ],
      })),
    [displayedItems, getOptions],
  );

  const selectedGiftCard = displayedItems.find(
    giftCard => giftCard.id === detailsGiftCardId,
  );

  return (
    <>
      <ReuseableTable
        name="Gift Cards"
        rows={rows}
        sort={sort}
        headerCells={[
          { id: 'customer', label: 'Customer' },
          { id: 'type', label: 'Type' },
          { id: 'purchasedAt', label: 'Purchased On' },
          { id: 'lastUsedAt', label: 'Last Used On' },
          { id: 'currentBalance', label: 'Balance Remaining' },
          { id: 'expiresAt', label: 'Expires' },
          { id: 'actions', label: '' },
        ]}
        emptyMessage="No gift cards found"
        loading={isLoading}
        pagination={
          shouldShowPagination
            ? {
                colSpan: 5,
                count: totalCount,
                rowsPerPage,
                page,
                setPage: onPageChange,
                hasNext: hasNextPage,
                hasPrevious: page > 0,
              }
            : undefined
        }
        headerAction={{
          label: 'Gift Card Settings',
          icon: <SettingsOutlinedIcon />,
          onClick: () => setIsSettingsOpen(true),
        }}
      />
      <GiftCardDetailsModal
        open={!!detailsGiftCardId}
        onClose={() => setDetailsGiftCardId(null)}
        giftCardId={detailsGiftCardId}
        options={selectedGiftCard ? getOptions(selectedGiftCard) : undefined}
      />
    </>
  );
}

export default GiftCardsTable;
